// ChatBar.js — the always-pinned multimodal entry point. Input only: the
// conversation itself lives in the ChatOverlay, which takes over the queue
// area when engaged (wake word / typing / mic) and auto-dismisses after
// inactivity. T→T: type + read. S→T/S→S: hold the mic.
import React, { useState } from 'react';
import { useComprehendLive } from 'agent/useComprehendLive';
import { T } from './liveTheme';

const ChatBar = () => {
  const { state, actions } = useComprehendLive();
  const [draft, setDraft] = useState('');

  const onChange = (e) => {
    setDraft(e.target.value);
    // Typing IS engagement: first keystroke summons the conversation layer.
    if (e.target.value) {
      if (state.chatOverlay) actions.bumpChatActivity();
      else actions.openChatOverlay();
    }
  };

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    actions.sendText(text);
  };

  const voiceLabel = state.voiceReplies
    ? state.anyRecording
      ? 'Voice replies on (during recording)'
      : 'Voice replies on'
    : state.anyRecording
      ? 'Text replies — muted for recording, tap to re-enable voice'
      : 'Voice replies off';

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          background: T.surfaceDeep,
          border: `1px solid ${T.border}`,
          borderRadius: 22,
          padding: '5px 6px 5px 14px',
          marginTop: 8,
        }}
      >
        <input
          value={draft}
          onChange={onChange}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder='Ask anything — or say "Hey Comprehend"'
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: T.textPrimary,
            fontSize: 12,
            minWidth: 0,
          }}
        />
        {state.ambientStatus === 'listening' && (
          <span
            onClick={actions.ambientCatchUp}
            role="button"
            aria-label="Catch up"
            title={
              state.ambientCatchingUp
                ? 'Catching up…'
                : '⚡ Catch up — act on everything just said'
            }
            style={{
              color: T.amber,
              fontSize: 13,
              cursor: state.ambientCatchingUp ? 'default' : 'pointer',
              flexShrink: 0,
              display: 'inline-flex',
              transform: state.ambientCatchingUp ? 'scale(1.12)' : 'scale(1)',
              transition: 'transform .2s ease',
            }}
          >
            <i
              className={`fa fa-bolt ${
                state.ambientCatchingUp ? 'cpl-glow' : ''
              }`}
              aria-hidden="true"
              style={
                state.ambientCatchingUp
                  ? { animation: 'cplPulse 0.8s ease-in-out infinite' }
                  : undefined
              }
            />
          </span>
        )}
        {/* Sustained mute — pause passive listening (ambient + wake word) for
            a demo. Lighter than the header's hard kill: no teardown, instant
            to toggle back. Always visible so you can mute before demoing. */}
        <span
          onClick={actions.toggleMicMuted}
          role="button"
          aria-label={state.micMuted ? 'Unmute microphone' : 'Mute microphone'}
          title={
            state.micMuted
              ? 'Mic muted — tap to resume listening'
              : 'Mute mic — stop it catching anything (no teardown)'
          }
          style={{
            color: state.micMuted ? T.redSoft : T.textFaint,
            fontSize: 13,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <i
            className={`fa ${
              state.micMuted ? 'fa-microphone-slash' : 'fa-microphone'
            }`}
            aria-hidden="true"
          />
        </span>
        <span
          onClick={actions.toggleSettings}
          title="Agent settings"
          style={{
            color: state.settingsOpen ? T.greenBright : T.textFaint,
            fontSize: 13,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <i className="fa fa-cog" aria-hidden="true" />
        </span>
        {state.agentStatus === 'listening' && (
          <span
            onClick={actions.muteMic}
            role="button"
            aria-label="Mute microphone"
            title="Mute — cut the mic and finish this utterance"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              borderRadius: '50%',
              background: 'rgba(245,54,92,0.18)',
              border: '1px solid rgba(245,54,92,0.55)',
              color: T.redSoft,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <i
              className="fa fa-microphone-slash"
              aria-hidden="true"
              style={{ fontSize: 13 }}
            />
          </span>
        )}
        <span
          onClick={actions.toggleVoiceReplies}
          title={voiceLabel}
          style={{
            color: state.voiceReplies ? T.greenBright : T.textFaint,
            fontSize: 13,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <i
            className={`fa ${
              state.voiceReplies ? 'fa-volume-up' : 'fa-volume-mute'
            }`}
            aria-hidden="true"
          />
        </span>
        {draft.trim() ? (
          <span
            onClick={send}
            role="button"
            aria-label="Send"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              borderRadius: '50%',
              background: T.green,
              color: T.greenDark,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <i
              className="fa fa-arrow-up"
              aria-hidden="true"
              style={{ fontSize: 13 }}
            />
          </span>
        ) : (
          <span
            className={
              state.agentStatus === 'listening' ? 'cpl-glow' : undefined
            }
            onMouseDown={actions.micDown}
            onMouseUp={actions.micUp}
            onTouchStart={actions.micDown}
            onTouchEnd={actions.micUp}
            role="button"
            aria-label="Hold to talk"
            title="Hold to talk"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // Synapse convention: BIG green = actively listening.
              width: state.agentStatus === 'listening' ? 36 : 30,
              height: state.agentStatus === 'listening' ? 36 : 30,
              borderRadius: '50%',
              background:
                state.agentStatus === 'listening' ? T.greenBright : T.green,
              color: T.greenDark,
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'all .15s ease',
            }}
          >
            <i
              className="fa fa-microphone"
              aria-hidden="true"
              style={{ fontSize: 13 }}
            />
          </span>
        )}
      </div>
      <p
        style={{
          margin: '5px 0 0',
          textAlign: 'center',
          fontSize: 9,
          color: T.textFaint,
        }}
      >
        {state.agentStatus === 'listening'
          ? 'listening…'
          : state.agentStatus === 'connecting'
            ? 'connecting…'
            : voiceLabel.toLowerCase()}
      </p>
    </div>
  );
};

export default ChatBar;
